import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { friendlyFromApiError } from '@/lib/errors';
import { useAuthStore } from '@/hooks/useAuthStore';

export default function Login() {
  const navigate = useNavigate();
  // C14-E.6 httpOnly: backend emits refresh token via Set-Cookie; body carries
  // only accessToken + staff. We therefore store only the access token here.
  const { setAccessToken, setStaff } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.login(email, password);

      if (response.data.requiresMfa) {
        setTempToken(response.data.tempToken);
      } else {
        setAccessToken(response.data.accessToken);
        setStaff(response.data.staff);
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      // D27: friendly mapping for known error codes/messages (e.g. TOTP_REPLAY)
      setError(friendlyFromApiError(err, 'Error al iniciar sesion'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.verifyMfa(tempToken!, mfaCode);
      setAccessToken(response.data.accessToken);
      setStaff(response.data.staff);
      navigate('/dashboard');
    } catch (err: unknown) {
      // D27: map TOTP_REPLAY and other MFA errors to Spanish user-friendly copy
      setError(friendlyFromApiError(err, 'Codigo MFA invalido'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="card max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">SolidCycle Admin</h1>
          <p className="text-gray-600 mt-2">Panel de Administracion</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {!tempToken ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="admin@solidcycle.com"
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="********"
                required
              />
            </div>

            <button type="submit" className="btn-primary w-full" disabled={isLoading}>
              {isLoading ? 'Iniciando sesion...' : 'Iniciar Sesion'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleMfaVerify} className="space-y-4">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-3">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold">Verificacion MFA</h2>
              <p className="text-sm text-gray-600">
                Ingresa el codigo de tu aplicacion autenticadora
              </p>
            </div>

            <div>
              <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-1">
                Codigo MFA
              </label>
              <input
                id="mfaCode"
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
              />
            </div>

            <button type="submit" className="btn-primary w-full" disabled={isLoading || mfaCode.length !== 6}>
              {isLoading ? 'Verificando...' : 'Verificar'}
            </button>

            <button
              type="button"
              onClick={() => {
                setTempToken(null);
                setMfaCode('');
              }}
              className="btn-secondary w-full"
            >
              Volver
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
